<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import debounce from 'lodash.debounce'
import { Spinner } from '@/components/ui'

interface FormattedResult {
  label: string
  subtext?: string
}

const props = defineProps<{
  modelValue: string
  placeholder?: string
  disabled?: boolean
  debounceMs?: number
  minLength?: number
  itemKey?: string
  getItemId: (item: unknown) => string
  getItemLabel: (item: unknown) => string
  fetchResults: (query: string) => Promise<unknown[]>
  formatResult?: (item: unknown) => FormattedResult
}>()

const { placeholder = 'Search...', disabled = false, debounceMs = 300, minLength = 2 } = props

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'select', item: unknown): void
}>()

// State
const query = ref('')
const results = ref<unknown[]>([])
const loading = ref(false)
const open = ref(false)
const highlightedIndex = ref(-1)
const error = ref<string | null>(null)

// Teleport positioning
const inputRef = ref<HTMLInputElement | null>(null)
const dropdownPosition = ref<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })
const flipUp = ref(false)

type State = 'idle' | 'searching' | 'results' | 'no-results' | 'error'

const state = computed<State>(() => {
  if (loading.value) return 'searching'
  if (error.value) return 'error'
  if (query.value.length < (props.minLength ?? 2)) return 'idle'
  if (results.value.length === 0) return 'no-results'
  return 'results'
})

watch(() => props.modelValue, (newVal) => {
  if (newVal === '') {
    query.value = ''
    results.value = []
    open.value = false
    highlightedIndex.value = -1
  }
})

watch(open, async (isOpen) => {
  if (isOpen) {
    await nextTick()
    updateDropdownPosition()
  }
})

const debouncedSearch = debounce(async (searchQuery: string) => {
  const min = props.minLength ?? 2
  if (searchQuery.length < min) {
    results.value = []
    open.value = false
    loading.value = false
    return
  }

  loading.value = true
  error.value = null

  try {
    const data = await props.fetchResults(searchQuery)
    results.value = data
    highlightedIndex.value = data.length > 0 ? 0 : -1
    open.value = true
  } catch (err) {
    error.value = (err as Error)?.message ?? 'Search failed'
    results.value = []
  } finally {
    loading.value = false
  }
}, props.debounceMs ?? 300)

onMounted(() => {
  window.addEventListener('scroll', closeOnScrollOrResize, true)
  window.addEventListener('resize', closeOnScrollOrResize, true)
})

onUnmounted(() => {
  debouncedSearch.cancel()
  window.removeEventListener('scroll', closeOnScrollOrResize, true)
  window.removeEventListener('resize', closeOnScrollOrResize, true)
})

function onQueryInput(event: Event) {
  const val = (event.target as HTMLInputElement).value
  query.value = val

  if (val.length < (props.minLength ?? 2)) {
    results.value = []
    open.value = false
    debouncedSearch.cancel()
    return
  }

  open.value = true
  debouncedSearch(val)
}

function onFocus() {
  if (query.value.length >= (props.minLength ?? 2) && results.value.length > 0) {
    open.value = true
  }
}

function onBlur() {
  setTimeout(() => {
    open.value = false
    highlightedIndex.value = -1
  }, 200)
}

function updateDropdownPosition() {
  if (!inputRef.value) return
  const inputRect = inputRef.value.getBoundingClientRect()
  dropdownPosition.value = {
    top: inputRect.bottom + 4,
    left: inputRect.left,
    width: inputRect.width,
  }
  flipUp.value =
    window.innerHeight - inputRect.bottom < 240 && inputRect.top > 240
}

const dropdownStyle = computed(() => {
  if (flipUp.value) {
    return {
      bottom: `${window.innerHeight - dropdownPosition.value.top + 4}px`,
      left: `${dropdownPosition.value.left}px`,
      width: `${dropdownPosition.value.width}px`,
    }
  }
  return {
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
    width: `${dropdownPosition.value.width}px`,
  }
})

function closeOnScrollOrResize() {
  if (open.value) {
    open.value = false
    highlightedIndex.value = -1
  }
}

function onKeydown(event: KeyboardEvent) {
  if (!open.value || results.value.length === 0) return

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, results.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      if (highlightedIndex.value >= 0 && highlightedIndex.value < results.value.length) {
        doSelect(results.value[highlightedIndex.value])
      }
      break
    case 'Escape':
      open.value = false
      highlightedIndex.value = -1
      break
  }
}

function doSelect(item: unknown) {
  const label = props.getItemLabel(item)
  query.value = label
  emit('update:modelValue', props.getItemId(item))
  emit('select', item)
  open.value = false
  highlightedIndex.value = -1
}

function formatItem(item: unknown): FormattedResult {
  if (props.formatResult) return props.formatResult(item)
  return { label: props.getItemLabel(item) }
}

function getItemKey(item: unknown, index: number): string {
  if (props.itemKey && typeof item === 'object' && item !== null) {
    const value = (item as Record<string, unknown>)[props.itemKey]
    if (value !== undefined) return String(value)
  }
  return String(index)
}
</script>

<template>
  <div class="async-autocomplete">
    <div class="input-wrap">
      <input
        ref="inputRef"
        :value="query"
        :placeholder="placeholder"
        :disabled="disabled"
        role="combobox"
        :aria-expanded="open"
        aria-autocomplete="list"
        :aria-activedescendant="highlightedIndex >= 0 ? `result-${highlightedIndex}` : undefined"
        autocomplete="off"
        @input="onQueryInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
      <span v-if="loading" class="spinner-wrap">
        <Spinner size="sm" />
      </span>
    </div>

    <!-- Results -->
    <Teleport to="body">
      <div
        v-if="open && results.length > 0"
        class="dropdown"
        :class="{ 'dropdown-flipped': flipUp }"
        role="listbox"
        :aria-label="placeholder"
        :style="dropdownStyle"
      >
        <div
          v-for="(item, index) in results"
          :id="`result-${index}`"
          :key="getItemKey(item, index)"
          role="option"
          :aria-selected="index === highlightedIndex"
          class="result-item"
          :class="{ highlighted: index === highlightedIndex }"
          @mousedown.prevent="doSelect(item)"
          @mouseenter="highlightedIndex = index"
        >
          <slot
            name="result-item"
            :item="item"
            :index="index"
            :highlighted="index === highlightedIndex"
            :format="formatItem(item)"
          >
            <div class="result-default">
              <span class="result-label">{{ formatItem(item).label }}</span>
              <span class="result-id">{{ getItemId(item) }}</span>
            </div>
            <span v-if="formatItem(item).subtext" class="result-subtext">
              {{ formatItem(item).subtext }}
            </span>
          </slot>
        </div>
      </div>
    </Teleport>

    <!-- No results -->
    <Teleport to="body">
      <div
        v-if="open && state === 'no-results'"
        class="dropdown dropdown-empty"
        role="listbox"
        aria-label="No results"
        :style="dropdownStyle"
      >
        <slot name="no-results">
          <div class="empty-state">No results found</div>
        </slot>
      </div>
    </Teleport>

    <!-- Error -->
    <Teleport to="body">
      <div
        v-if="open && state === 'error'"
        class="dropdown dropdown-empty"
        role="alert"
        :style="dropdownStyle"
      >
        <slot name="error">
          <div class="error-state">{{ error ?? 'Search failed' }}</div>
        </slot>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.async-autocomplete {
  position: relative;
  width: 100%;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrap input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: var(--radius-md, 0.375rem);
  font-size: 0.875rem;
  background: var(--color-surface, #fff);
  color: var(--color-ink, #111827);
  font-family: inherit;
}

.input-wrap input:focus {
  outline: none;
  border-color: var(--color-primary, #3b82f6);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.input-wrap input:disabled {
  background: var(--color-surface-muted, #f3f4f6);
  cursor: not-allowed;
  opacity: 0.7;
}

.spinner-wrap {
  position: absolute;
  right: 0.75rem;
  display: flex;
  align-items: center;
  color: var(--color-ink-soft, #6b7280);
}

.dropdown {
  position: fixed;
  z-index: 60;
  max-height: min(300px, 60vh);
  overflow-y: auto;
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: var(--radius-md, 0.375rem);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.dropdown-empty {
  padding: 0.5rem;
}

.result-item {
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--color-border-soft, #f3f4f6);
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.result-item:last-child {
  border-bottom: none;
}

.result-item:hover,
.result-item.highlighted {
  background: var(--color-surface-muted, #f3f4f6);
}

.result-default {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.result-label {
  font-weight: 500;
  color: var(--color-ink, #111827);
}

.result-id {
  font-family: monospace;
  font-size: 0.75rem;
  color: var(--color-ink-soft, #6b7280);
  background: var(--color-surface-muted, #f3f4f6);
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.result-subtext {
  font-size: 0.75rem;
  color: var(--color-ink-soft, #6b7280);
}

.empty-state,
.error-state {
  font-size: 0.875rem;
  color: var(--color-ink-soft, #6b7280);
  padding: 0.5rem;
  text-align: center;
}

.error-state {
  color: var(--color-error, #dc2626);
}
</style>
